import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FormSelector from '../components/FormElements/FormSelector';
import Loading from '../components/Loading';
import { Form, Formik } from "formik";
import { userFilterSchema } from '../../common/validators/userInsertUpdateSchema';
import FormObserver from '../components/FormElements/FormObserver';
import { FormCheckbox, FormInput } from '../components/FormElements';
import FormMultiSelect from '../components/FormElements/FormMultiSelect';
import Button from '../components/Elements/Button';
import commonNetwork from '../../common/api/commonNetwork';
import { toast } from 'react-toastify';
import { permissions } from '../../constants/options/userType';
import { OptionModel } from '../../common/types/OptionModel';
import useGetCustomerName from '../../hooks/useGetCustomerName';
import { ApiResponseType } from '../../common/api/apiHelper';
import { getNavigationUrl } from '../../common/helpers/navigationHelpers';
import { successMessages } from '../../constants/messages/successMessages';
import PageTitle from '../components/PageTitle';
import { pageTitleTexts } from '../../constants/texts/pageTitleTexts';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { refreshUserTable } from '../../common/helpers/refreshTables/refreshUserTable';
import FormMaskedInput from '../components/FormElements/FormMaskedInput';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { UserOperationTypes } from '../../common/types/AuthorizationProps';
import { getSelectedValueFromId } from '../../common/helpers/selectBoxHelper';
import useGetUserTypes from '../../hooks/useGetUserTypes';

type UserDetailProp = {
  userCode: string,
  userName: string,
  userPassword: string,
  userEmail: string,
  userPhone: string,
  userDesc: string,
  status: boolean,
  customerUserList: Array<OptionModel>,
  userType: any,
  defineCustomer: boolean,
  paymentCollection: boolean,
  freePaymentCollection: boolean,
  sendPaymentLink: boolean,
  paymentCancellation: boolean,
  paymentRefund: boolean,
  transactionList: boolean,
  bankSelection: boolean,
  erpIntegration: boolean,
  erpBalanceInquiry: boolean,
  userOperations: boolean,
  isSingleCustomerCheck: boolean,
  customerToLinkPayments: number,
  parentUser: number,
  erpSalesmanCode: string,
  bankId: number,
  [key: string]: any
}

const InsertUpdateUser = () => {

  usePermissionCheck(UserOperationTypes["Kullanıcı İşlemleri"], "Kullanıcı İşlemleri izni yok");
  let { userId }: any = useParams();
  const loadActive = userId > 0;

  // useState's
  const [formValues, setFormValues] = useState<UserDetailProp>({
    userCode: "",
    userName: "",
    userPassword: "********",
    userEmail: "",
    userPhone: "",
    userDesc: "",
    status: false,
    customerUserList: [],
    userType: "",
    defineCustomer: false,
    paymentCollection: false,
    freePaymentCollection: false,
    sendPaymentLink: false,
    paymentCancellation: false,
    paymentRefund: false,
    transactionList: false,
    bankSelection: false,
    erpIntegration: false,
    erpBalanceInquiry: false,
    userOperations: false,
    isSingleCustomerCheck: false,
    customerToLinkPayments: 0,
    parentUser: 0,
    erpSalesmanCode: "",
    bankId: 0,
    commissionSelection: false
  });
  const [postLoading, setPostLoading] = useState(false);
  const [getLoading, setGetLoading] = useState(loadActive);
  const [customerOptions, setCustomerOptions] = useState<OptionModel[]>([]);
  const [selectedUserType, setSelectedUserType] = useState<any>(0);
  const [permissionsState, setPermissionsState] = useState<any[]>([]);
  const [isSingleCustomerCheckStatus, setIsSingleCustomerCheckStatus] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [counter, setCounter] = useState<number>(-2);
  const [parentUserOptions, setParentUserOptions] = useState<OptionModel[]>([]);
  const [customerLoading, setCustomerLoading] = useState<boolean>(false);
  const [parentUserLoading, setParentUserLoading] = useState<boolean>(false);
  const [posList, setPosList] = useState<any>([]);
  const [userType, setUserType] = useState<OptionModel[]>([]);
  const [userTypeLoading, setUserTypeLoading] = useState<boolean>(false);

  const backToTheUserUrl = getNavigationUrl("/kullanici-listesi");
  const navigate = useNavigate();
  const { firmname } = useParams();

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // useEffect's
  useGetCustomerName(setCustomerOptions, setCustomerLoading);
  useGetUserTypes(setUserType, setUserTypeLoading)

  useEffect(() => {
    setSelectedUserType(formValues.userType)
  }, [formValues.userType])

  useEffect(() => {
    setIsSingleCustomerCheckStatus(formValues.isSingleCustomerCheck);
  }, [formValues.isSingleCustomerCheck])

  useEffect(() => {
    onSelectedIsSingleCustomer();
  }, [isSingleCustomerCheckStatus])

  useEffect(() => {
    if (userId > 0) {
      selectUserInformation();
    }
  }, [userId]);

  useEffect(() => {
    onSelectedUserType();
    handlePermission();
  }, [selectedUserType]);

  useEffect(() => {
    const defaultPermissions = permissions
      .filter(permission => formValues[permission.formname])
      .map(permission => permission.value);
    setSelectedPermissions(defaultPermissions);
  }, [formValues]);

  useEffect(() => {
    if (formValues.userType == 5) {
      getParentUsers();
    }
  }, [formValues.userType]);

  useEffect(() => {
    fetchPos();
  }, []);

  const selectUserInformation = async () => {
    let result: ApiResponseType;
    let userData: UserDetailProp;

    const parameters = {
      userId: userId
    }
    result = await commonNetwork.get("/user/" + userId, parameters);
    if (result.isSuccess) {
      if (result.data.userTypeId == 1) {
        toast.error("Kullanıcı bilgilerini görüntüleyemezsiniz. Bu kullanıcı bir admin kullanıcısıdır.");
        navigate(getNavigationUrl("/kullanici-listesi"));
      }
      const data = result.data;
      userData = {
        userCode: data.userCode != null ? data.userCode : "",
        userName: data.userName != null ? data.userName : "",
        userPassword: "********",
        userEmail: data.userEmail != null ? data.userEmail : "",
        userPhone: data.userPhone != null ? data.userPhone.startsWith('0') ? data.userPhone.slice(1) : data.userPhone : "",
        userDesc: data.userDesc != null ? data.userDesc : "",
        status: false,
        customerUserList: data.customerUserList.map((customer: any) => ({
          value: customer.id,
          label: customer.label
        })),
        userType: data.userTypeId,
        defineCustomer: data.permissions.includes(1) ? true : false,
        paymentCollection: data.permissions.includes(2) ? true : false,
        freePaymentCollection: data.permissions.includes(3) ? true : false,
        sendPaymentLink: data.permissions.includes(4) ? true : false,
        paymentCancellation: data.permissions.includes(5) ? true : false,
        paymentRefund: data.permissions.includes(6) ? true : false,
        transactionList: data.permissions.includes(7) ? true : false,
        bankSelection: data.permissions.includes(8) ? true : false,
        erpIntegration: data.permissions.includes(9) ? true : false,
        erpBalanceInquiry: data.permissions.includes(10) ? true : false,
        userOperations: data.permissions.includes(11) ? true : false,
        customerList: data.permissions.includes(12) ? true : false,
        isSingleCustomerCheck: data.isSingleCustomerCheck,
        customerToLinkPayments: data.customerSyncId,
        parentUser: data.parentUserId,
        erpSalesmanCode: data.erpSalesmanCode,
        bankId: data.bankId,
        commissionSelection: data.permissions.includes(13) ? true : false
      }

      setFormValues(userData);
    } else {
      if (result.errorMessage == null) {
        toast.error("Kullanıcı bilgileri alınamadı. Lütfen tekrar deneyin.");
      } else {
        toast.error(result.errorMessage);
      }
      navigate(getNavigationUrl("/kullanici-listesi"));
    }
    setGetLoading(false);
  }

  const onSubmit = async () => {
    const parameters: any = {
      username: formValues.userName,
      password: formValues.userPassword === "********" ? null : formValues.userPassword,
      userCode: formValues.userCode,
      userDesc: formValues.userDesc,
      email: formValues.userEmail,
      ccmail: "",
      phone: formValues.userPhone
        ?.replace("(", "")
        .replace(")", "")
        .replace(/\s+/g, ""),
      isSyncCustomer: formValues.isSingleCustomerCheck,
      customerSyncCode: formValues.customerToLinkPayments,
      selectedUserType: formValues.userType,
      selectedParent: formValues.parentUser,
      permissions: selectedPermissions,
      customerUserList: formValues.customerUserList,
      erpSalesmanCode: formValues.erpSalesmanCode,
      customerList: formValues.customerList,
      id: userId,
      bankId: formValues.bankId == null ? 0 : formValues.bankId
    }

    if (userId > 1) {
      parameters.id = parseInt(userId);
    }

    setPostLoading(true);
    let result: ApiResponseType;
    try {
      if (userId > 0) {
        result = await commonNetwork.post(`/user/update-user`, parameters);
      } else {
        result = await commonNetwork.post("/user/", parameters);
      }

      if (result.isSuccess) {
        refreshUserTable(dispatch);
        toast.success(userId > 0 ? successMessages.update : successMessages.add);
        navigate(getNavigationUrl("/kullanici-listesi"));
      } else {
        toast.error(result.errorMessage);
      }
    } catch (error) {
      toast.error("Hata: " + error + "\nİşlemi tekrar deneyin ya da çözüm için desteğe ulaşın.");
    } finally {
      setPostLoading(false);
    }
  }

  const onUpdate = (value: UserDetailProp) => {
    setFormValues({ ...formValues, ...value });
  };

  function handlePermission() {
    let newPermissions: OptionModel[];
    switch (selectedUserType) {
      case 1: // admin
        newPermissions = permissions;
        break;
      case 2: // firma yetkilisi
        newPermissions = permissions;
        break;
      case 3: //musteri temsilcisi
        newPermissions = permissions.filter(item => item.tier !== 3 && item.tier !== 0);
        break;
      case 4: //bayi müsteri
      case 5: // altbayi
        newPermissions = permissions.filter(item => item.tier === 1);
        break;
      default:
        newPermissions = [];
        break;
    }
    setPermissionsState(newPermissions);
  }

  function onSelectedUserType() {
    const defaultValues = {
      defineCustomer: false,
      paymentCollection: false,
      freePaymentCollection: false,
      sendPaymentLink: false,
      paymentCancellation: false,
      paymentRefund: false,
      transactionList: false,
      bankSelection: false,
      erpIntegration: false,
      erpBalanceInquiry: false,
      userOperations: false,
      isSingleCustomerCheck: false,
      customerToLinkPayments: 0,
      parentUser: 0
    };

    const userTypeSpecificValues = (type: any) => {
      switch (type) {
        case 2:
          return {
            paymentCollection: true,
            freePaymentCollection: true,
            sendPaymentLink: true,
            paymentCancellation: true,
            paymentRefund: true,
            erpIntegration: true,
            erpBalanceInquiry: true,
            customerList: true
          };
        case 3:
          return {
            paymentCollection: true,
            freePaymentCollection: true,
            sendPaymentLink: true,
            erpBalanceInquiry: true
          };
        case 4:
        case 5:
          return {
            paymentCollection: true,
            erpBalanceInquiry: true
          };
        default:
          return {};
      }
    };

    if (userId > 0) {
      if (counter <= 0) {
        setCounter(counter + 1);
        return;
      } else {
        const newValues = selectedUserType ? { ...defaultValues, ...userTypeSpecificValues(selectedUserType) } : defaultValues;
        setFormValues((prevValues) => ({ ...prevValues, ...newValues }));
      }
    } else {
      const newValues = selectedUserType ? { ...defaultValues, ...userTypeSpecificValues(selectedUserType) } : defaultValues;
      setFormValues((prevValues) => ({ ...prevValues, ...newValues }));
    }
  }


  function onSelectedIsSingleCustomer() {
    if (!isSingleCustomerCheckStatus) {
      setFormValues((prevValues) => ({
        ...prevValues,
        isSingleCustomerCheck: false,
        customerToLinkPayments: 0,
        parentUser: 0
      }));
    }
  }

  const handleCheckboxChange = (permissionValue: number) => {
    setSelectedPermissions(prevState =>
      prevState.includes(permissionValue)
        ? prevState.filter(value => value !== permissionValue)
        : [...prevState, permissionValue]
    );
  };

  function goToBack() {
    navigate(backToTheUserUrl);
  }


  if (getLoading) {
    return (
      <Loading />
    );
  }

  const getParentUsers = async () => {
    try {
      setParentUserLoading(true);
      let result: ApiResponseType;
      result = await commonNetwork.get("/user/get-parent-users");
      if (result.isSuccess) {
        const parentUserData = result.data;
        const options: OptionModel[] = parentUserData.map((item: any) => ({
          value: item.userId,
          label: item.userDesc ? item.userName + " - " + item.userDesc : item.userName
        }));
        setParentUserOptions(options);
      } else {
        toast.error(result.errorMessage);
      }
    } catch (error) {
      toast.error("An error occurred while fetching parent users.");
    } finally {
      setParentUserLoading(false);
    }
  }

  async function fetchPos() {
    const data = await commonNetwork.get("/virtualpos");
    var poslist = [];
    if (data.isSuccess) {
      var res = data.data.data;
      for (let i = 0; i < res.length; i++) {
        if (res[i].status == true) {
          const element = res[i];
          poslist.push({
            value: element.vpid,
            label: element.vpdisplayname ? element.vpdisplayname : element.bankname,
          });
        }
      }
      setPosList(poslist);

    } else {
      toast.error(data.errorMessage);
    }
  }

  return (
    getLoading ? (
      <Loading desc={userId > 0 ? 'Kullanıcı bilgileri yükleniyor...' : 'Kullanıcı kayıt sayfası yükleniyor...'} />
    ) : (
      <div className='pb-7'>
        <PageTitle title={userId > 0 ? pageTitleTexts.updateUser : pageTitleTexts.insertUser} />
        <Formik
          initialValues={formValues}
          validationSchema={userFilterSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {({
            values,
            touched,
            errors,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (<Form>
              <FormObserver watch={(val: any) => onUpdate(val)} />

              <div className='w-full'>
                <div className="px-4 py-2 flex flex-col justify-start space-y-2">
                  <div className="flex flex-row items-center">
                    <img src={"/icons/add-user.svg"} className="h-6 w-6 black" />
                    <span className="text-lg font-bold leading-7 text-contrast-90 sm:truncate ml-2">
                      {userId > 0 ? "Kullanıcı Güncelleme Ekranı" : "Kullanıcı Kayıt Ekranı"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mx-4 px-4 py-2 bg-white shadow-md mb-4 pb-4 mt-1 rounded-md">

                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                  <div className="mt-3">
                    <h1 className="font-bold text-contrast-90">Kullanıcı Tipi</h1>
                    <span className="text-contrast-50 text-md">
                      Kullanıcı tipini seçiniz.
                    </span>
                  </div>
                  <div className='mt-3'>
                    <FormSelector
                      options={userType}
                      name="userType"
                      placeholder="Kullanıcı tipi seç"
                      label="Kullanıcı Tipi*"
                      isSearchable={false}
                      value={userType.find((item) => item.value == formValues.userType)}
                      isClearable
                      onChange={() => { }}
                      loading={userTypeLoading}
                    />
                  </div>
                  <div className={formValues.userType === 5 ? 'mt-3' : "hidden"}>
                    <FormSelector
                      options={parentUserOptions}
                      name="parentUser"
                      value={formValues.parentUser ? parentUserOptions.find(item => item.value == formValues.parentUser) : null}
                      placeholder="Üst bayi seç"
                      label="Üst Bayi"
                      isSearchable
                      isClearable
                      lazyPageSize={50}
                      loading={parentUserLoading}
                    />
                  </div>
                </div>

                <hr />

                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                  <div className="mt-3">
                    <h1 className="font-bold text-contrast-90">Kullanıcı Bilgileri</h1>
                    <span className="text-contrast-50 text-md">
                      Kullanıcıların sisteme girişi için gerekli olan bilgileri giriniz.
                    </span>
                  </div>

                  <div className="w-full col-span-2">
                    <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                      <div className="mt-3">
                        <FormInput
                          label="Kullanıcı Adı*"
                          name="userName"
                          type="text"
                          maxLength={101}
                        />
                      </div>
                      <div className="mt-3">
                        <FormInput
                          label="Kullanıcı Şifresi*"
                          name="userPassword"
                          type="password"
                          maxLength={51}
                          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            if (e.target.value === "********") {
                              e.target.value = "";
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                      <div className="mt-3">
                        <FormInput
                          label={formValues.userType == 4 || formValues.userType == 5 ? "Bayi - Müşteri Ünvanı" : "Kullanıcı Açıklaması"}
                          name="userDesc"
                          type="text"
                          maxLength={101}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                      <div className="mt-3">
                        <FormInput
                          label="Kullanıcı Kodu*"
                          name="userCode"
                          type="text"
                          maxLength={51}
                        />
                      </div>
                      <div className='mt-3'>
                        <FormInput
                          label="Erp Satış Temsilcisi Kodu"
                          name="erpSalesmanCode"
                          type="text"
                          maxLength={51}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                  <div className="mt-3">
                    <h1 className="font-bold text-contrast-90">İletişim Bilgileri</h1>
                    <span className="text-contrast-50 text-md">
                      Kullanıcının E-Posta Adresi ve Cep Telefonu bilgisini giriniz.
                    </span>
                  </div>
                  <div className="w-full col-span-2">
                    <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                      <div className="mt-3">
                        <FormInput
                          label="E-Posta Adresi*"
                          name="userEmail"
                          type="text"
                          maxLength={151}
                        />
                      </div>
                      <div className="mt-3">
                        {/* <FormInput
                          label="Cep Telefonu*"
                          name="userPhone"
                          type="text"
                          placeholder="5xx xxx xx xx"
                          mask={formValues.userPhone ? (formValues.userPhone.startsWith("0") ? "9(999) 999 99 99" : "(999) 999 99 99") : "(999) 999 99 99"}
                        /> */}
                        <FormMaskedInput
                          label="Cep Telefonu*"
                          name="userPhone"
                          maskOptions={{
                            delimiter: ' ',
                            blocks: [3, 3, 2, 2],
                            numericOnly: true
                          }}
                          placeholder="5xx xxx xx xx"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <hr />

                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                  <div className="mt-3">
                    <h1 className="font-bold text-contrast-90">Varsayılan Ödeme Altyapısı</h1>
                    <span className="text-contrast-50 text-md">
                      Kullanıcının ödeme işlemlerinde kullanacağı varsayılan ödeme altyapısını seçiniz.
                    </span>
                  </div>
                  <div className='mt-3'>
                    <FormSelector
                      name="bankId"
                      options={posList}
                      label="Ödeme Altyapısı"
                      placeholder="Ödeme altyapısı seç"
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      error={errors.bankId}
                      touched={touched.bankId}
                      defaultValue={formValues.bankId}
                      value={getSelectedValueFromId(values.bankId, posList)}
                      isClearable
                    />
                  </div>
                </div>

                <hr />

                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                  <div className="mt-3">
                    <h1 className="font-bold text-contrast-90">Kullanıcı – Müşteri Yetkilendirme</h1>
                    <span className="text-contrast-50 text-md">
                      Kullanıcının ödeme alma yetkisi olan müşterileri seçiniz.
                    </span>
                  </div>
                  <div className='mt-3'>
                    <FormMultiSelect
                      options={customerOptions}
                      name="customerUserList"
                      placeholder="Müşteri seç"
                      label="Bağlı Olduğu Müşteriler"
                      value={customerOptions.filter((item: any) => formValues.customerUserList.includes(item))}
                      loading={customerLoading}
                    />
                  </div>
                </div>

                <div className={formValues.userType === 4 || formValues.userType === 5 ? "" : "hidden"}>
                  <hr />
                  <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                    <div className="mt-3">
                      <h1 className="font-bold text-contrast-90">Ödemeler Tek Müşteri Hesabına Bağlansın Mı?</h1>
                      <span className="text-contrast-50 text-md">
                        Müşteri hesabı seçiniz.
                      </span>
                    </div>
                    <div className='mt-4'>
                      <FormCheckbox
                        key={1}
                        label={"Evet"}
                        name="isSingleCustomerCheck"
                        type="checkbox"
                        onChange={() => { }}
                      />
                    </div>
                    <div className={!formValues.isSingleCustomerCheck ? "hidden" : 'mt-4'}>
                      <FormSelector
                        options={customerOptions}
                        name="customerToLinkPayments"
                        value={formValues.customerToLinkPayments ? customerOptions.find(item => item.value == formValues.customerToLinkPayments) : null}
                        placeholder="Müşteri seç"
                        label="Ödemenin Bağlı Olduğu Müşteri"
                        isSearchable
                        isClearable
                        isLazyFrontend
                        lazyPageSize={50}
                        loading={customerLoading}
                      />
                    </div>
                  </div>
                </div>

                <hr />

                <div className={formValues.userType ? "" : "hidden"}>
                  <div className='px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4'>
                    <div className="mt-3">
                      <h1 className="font-bold text-contrast-90">Kullanıcı Yetkileri</h1>
                      <span className="text-contrast-50 text-md">Kullanıcı yetkilerini seçiniz.</span>
                    </div>
                  </div>

                  <div className="px-4 grid grid-cols-1 gap-4 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                    {permissionsState.map((permission) => (
                      <div className={permission.formname === "defineCustomer" ? "hidden" : ""} key={permission.value}>
                        <FormCheckbox
                          key={permission.value}
                          name={permission.formname}
                          label={permission.label}
                          type="checkbox"
                          onChange={() => handleCheckboxChange(permission.value)}
                          defaultChecked={selectedPermissions.includes(permission.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <hr />

                <div className="mt-3 justify-end mr-3 flex flex-row">
                  <Button
                    type="button"
                    color="gray"
                    hover="primary"
                    size="xs"
                    callback={goToBack}
                    className="mr-2"
                  >
                    Geri Dön
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    hover="primary"
                    size="xs"
                    disabled={postLoading}
                    loading={postLoading}
                  >
                    {userId > 0 ? "Güncelle" : "Kaydet"}
                  </Button>
                </div>
              </div>
            </Form>)
          }}
        </Formik>
      </div>
    )
  );
};

export default InsertUpdateUser;
